import {
  pipe,
  reduce,
  add,
  map,
  prop,
  findIndex,
  propEq,
} from 'ramda';

import {
  FETCH_MESSAGES,
  FETCH_MESSAGES_SUCCESS,
  FETCH_MESSAGES_ERROR,
  CHANGE_ACTIVE_FILTER,
  DELETE_NOTIFICATIONS,
  DELETE_NOTIFICATIONS_SUCCESS,
  DELETE_NOTIFICATIONS_ERROR,
  MARK_NOTIFICATION_AS_READ,
  MARK_NOTIFICATION_AS_READ_SUCCESS,
  MARK_NOTIFICATION_AS_READ_ERROR,
  GET_REQUEST_DETAILS,
  GET_REQUEST_DETAILS_SUCCESS,
  GET_REQUEST_DETAILS_ERROR,
  PERIOD_MONTH,
  PERIOD_YEAR,
  UPDATE_PERIOD,
  RESET_ERROR,
  MAX_TITLE_LENGTH,
} from './constants';

import {
  isNotification,
  isPermissionMessage,
  setNotificationRead,
  convertAPIDateToUIDate,
  defaultComparator,
  isExpandable,
} from './common';

const initialState = {
  isFetching: false,
  isNotificationUpdating: false,
  isDeleting: false,
  isDetailsFetching: false,
  error: null,
  filters: null,
  activeFilter: null,
  items: null,
  requestStatuses: null,
  notificationStatuses: null,
  period: PERIOD_MONTH,
  table: {
    data: null,
  },
  isNotAllMessages: false,
  filtersLength: null,
};

const initialSort = (tableData) => [...tableData].sort(defaultComparator);

const addRequestDetails = (messages, { requestId, requestDetails }) => {
  const messageIndex = findIndex(propEq('id', requestId), messages);
  const message = messages[messageIndex];

  if (message) {
    return [
      ...messages.slice(0, messageIndex),
      {
        ...message,
        statusCode: requestDetails.answer.crmStatusText,
        id: requestDetails.reqExternalId,
        comments: requestDetails,
      },
      ...messages.slice(messageIndex + 1),
    ];
  }

  return messages;
};

const getSelectionValue = (notification) => (notification ? false : undefined);

const getSelection = pipe(
  isNotification,
  getSelectionValue,
);

const calcTotalCount = pipe(
  map(prop('count')),
  reduce(add, 0),
);

// TODO: add title localization
const getAllFilter = ({ filters }) => ({
  title: 'Все',
  isAll: true,
  count: calcTotalCount(filters),
});

const addAllFilter = (state) => {
  const allFilter = getAllFilter(state);

  return {
    ...state,
    filters: [
      allFilter,
      ...state.filters,
    ],
    activeFilter: allFilter,
  };
};

const getStatusText = (code, statuses) => prop('value', statuses.find((status) => status.code === code) || {});

const getStatus = (message, requestStatuses, notificationStatuses, permissionRequestStatuses) => {
  if (message.isHelpdesk) {
    if (message.comments
        && message.comments.answer
        && message.comments.answer.crmStatusText) {
      return message.comments.answer.crmStatusText;
    }

    return '';
  }

  if (isNotification(message)) {
    return getStatusText(message.statusCode, notificationStatuses);
  }

  if (isPermissionMessage(message)) {
    return getStatusText(message.statusCode, permissionRequestStatuses);
  }

  return getStatusText(message.statusCode, requestStatuses);
};

const getMessageTitle = (message) => {
  const title = message.title || `${message.text.replace(/(<([^>]+)>)/ig, '').slice(0, MAX_TITLE_LENGTH)}...`;

  return isExpandable(message) ? title : `${title}${message.id ? ` ID ${message.id}` : ''}`;
};

const getTableData = (messages, requestStatuses, notificationStatuses, permissionRequestStatuses) => messages
  .map((message) => ({
    ...message,
    statusCode: getStatus(message, requestStatuses, notificationStatuses, permissionRequestStatuses),
    date: convertAPIDateToUIDate(message.date),
    selected: getSelection(message),
    title: getMessageTitle(message),
  }));

const filterMessages = (messages, activeFilter) => messages
  .filter((message) => message.filterCode === activeFilter.code);

const applyFilter = (state) => {
  const filteredMessages = state.activeFilter.isAll
    ? state.items
    : filterMessages(state.items, state.activeFilter);
  const sortedMessages = initialSort(filteredMessages);
  const tableData = getTableData(
    sortedMessages,
    state.requestStatuses,
    state.notificationStatuses,
    state.permissionRequestStatuses,
  );

  return {
    ...state,
    table: {
      ...state.table,
      data: tableData,
      isAllRowsSelected: false,
    },
  };
};

const refreshFilter = pipe(addAllFilter, applyFilter);

const markNotificationAsRead = (messages, id, statusMap) => {
  const messageIndex = findIndex(propEq('id', id), messages);
  const message = messages[messageIndex];

  if (message) {
    const readMessage = setNotificationRead(message);

    return [
      ...messages.slice(0, messageIndex),
      {
        ...readMessage,
        statusCode: statusMap
          ? getStatusText(readMessage.statusCode, statusMap)
          : readMessage.statusCode,
      },
      ...messages.slice(messageIndex + 1),
    ];
  }

  return messages;
};

export default function (state = initialState, action) {
  switch (action.type) {
    case FETCH_MESSAGES:
      return {
        ...state,
        isFetching: true,
      };
    case FETCH_MESSAGES_SUCCESS:
      return state.filtersLength === action.data.filters.length ? applyFilter({
        ...state,
        isFetching: false,
        items: action.data.messages,
        requestStatuses: action.data.requestStatuses,
        notificationStatuses: action.data.notificationStatuses,
        permissionRequestStatuses: action.data.permissionRequestStatuses,
        isNotAllMessages: action.data.isNotAllMessages,
      }) : refreshFilter({
        ...state,
        isFetching: false,
        filters: action.data.filters,
        filtersLength: action.data.filters.length,
        items: action.data.messages,
        requestStatuses: action.data.requestStatuses,
        notificationStatuses: action.data.notificationStatuses,
        permissionRequestStatuses: action.data.permissionRequestStatuses,
        isNotAllMessages: action.data.isNotAllMessages,
      });
    case FETCH_MESSAGES_ERROR:
      return {
        ...state,
        isFetching: false,
        error: action.error,
        items: null,
        requestStatuses: null,
        notificationStatuses: null,
        filters: null,
        activeFilter: null,
        table: {
          ...state.table,
          data: null,
        },
      };
    case CHANGE_ACTIVE_FILTER:
      return applyFilter({
        ...state,
        activeFilter: action.data,
      });
    case DELETE_NOTIFICATIONS:
      return {
        ...state,
        isDeleting: true,
      };
    case DELETE_NOTIFICATIONS_SUCCESS:
      return {
        ...state,
        isDeleting: false,
      };
    case DELETE_NOTIFICATIONS_ERROR:
      return {
        ...state,
        isDeleting: false,
        error: action.error,
      };
    case MARK_NOTIFICATION_AS_READ:
      return {
        ...state,
        isNotificationUpdating: true,
      };
    case MARK_NOTIFICATION_AS_READ_SUCCESS:
      return {
        ...state,
        isNotificationUpdating: false,
        items: markNotificationAsRead(state.items, action.data),
        table: {
          ...state.table,
          data: markNotificationAsRead(
            state.table.data,
            action.data,
            state.notificationStatuses,
          ),
        },
      };
    case MARK_NOTIFICATION_AS_READ_ERROR:
      return {
        ...state,
        isNotificationUpdating: false,
        error: action.error,
      };
    case GET_REQUEST_DETAILS:
      return {
        ...state,
        isDetailsFetching: true,
      };
    case GET_REQUEST_DETAILS_SUCCESS:
      return {
        ...state,
        isDetailsFetching: false,
        items: addRequestDetails(state.items, action.data),
        table: {
          ...state.table,
          data: addRequestDetails(state.table.data, action.data),
        },
      };
    case GET_REQUEST_DETAILS_ERROR:
      return {
        ...state,
        isDetailsFetching: false,
        error: action.error,
      };
    case UPDATE_PERIOD:
      return {
        ...state,
        period: PERIOD_YEAR,
      };
    case RESET_ERROR:
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
}
